'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Box } from '@virginmediao2/momentum-nextjs/box';
import { Button } from '@virginmediao2/momentum-nextjs/button';
import {
  Card,
  CardFooter,
  CardHeader,
} from '@virginmediao2/momentum-nextjs/card';
import { Divider } from '@virginmediao2/momentum-nextjs/divider';
import { Flex } from '@virginmediao2/momentum-nextjs/flex';
import {
  ErrorAlertsRef,
  FormInput,
  FormRadioGroup,
  FormRadioInput,
  FormSelect,
  FormTextarea,
} from '@virginmediao2/momentum-nextjs/forms';
import { Icon, IconProps } from '@virginmediao2/momentum-nextjs/icon';
import { Text } from '@virginmediao2/momentum-nextjs/text';
import { useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  FalBriefcase,
  FalCalendarDays,
  FalCreditCard,
} from '@virginmediao2/momentum-icons/icons/fal';

import { sendComplaintsFormRequest } from '@/lib/complaints-form';
import { mobileNumberRegex } from '@/lib/utils/regex-validators';

import styles from './complaints-form.module.css';
import { complaintsList } from './complaintsList';

const SUBMISSION_SUCCESS_URL =
  'https://www.o2.co.uk/how-to-complain/complaints-form/thank-you';

const complaintsFormSchema = z
  .object({
    isCustomer: z.string({
      required_error: 'Please specify if you are a customer',
      invalid_type_error: 'Please specify if you are a customer',
    }),
    mobileNumber: z.string().regex(mobileNumberRegex, 'Invalid mobile number'),
    complaintRadio: z.string(),
    issueDescription: z.string(),
    issueType: z.string(),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    addressLine1: z.string().min(1, 'Address line 1 is required'),
    addressLine2: z.string().optional(),
    town: z.string().min(1, 'Town is required'),
    country: z.string().min(1, 'Country is required'),
    postCode: z.string().min(1, 'Postcode is required'),
    issueDetails: z.string().min(50, 'The details requires at least 10 words'),
    howToFixTheIssue: z.string().min(1, 'How to fix the issue is required'),
    contactMobileNumber: z
      .string()
      .regex(mobileNumberRegex, 'Invalid mobile number'),
    email: z
      .string()
      .optional()
      .refine((val) => !val || z.string().email().safeParse(val).success, {
        message: 'Invalid email address',
      }),
    confirmEmail: z
      .string()
      .optional()
      .refine((val) => !val || z.string().email().safeParse(val).success, {
        message: 'Invalid email address',
      }),
    preferContact: z.string(),
  })
  .superRefine(({ email, confirmEmail }, ctx) => {
    if (email && confirmEmail && email !== confirmEmail) {
      ctx.addIssue({
        code: 'custom',
        path: ['confirmEmail'],
        message: 'Email addresses do not match',
      });
    }
  });

enum optionNames {
  payMonthly = 'payMonthly',
  business = 'business',
  payAsYouGo = 'payAsYouGo',
}

const formOptionInputList: Array<{
  name: optionNames;
  label: string;
  icon: IconProps['icon'];
}> = [
  {
    name: optionNames.payMonthly,
    label: 'Pay Monthly',
    icon: FalCalendarDays,
  },
  {
    name: optionNames.business,
    label: 'Business',
    icon: FalBriefcase,
  },
  {
    name: optionNames.payAsYouGo,
    label: 'Pay as You Go',
    icon: FalCreditCard,
  },
];

const Options = ({
  optionsData,
}: {
  optionsData: Record<string, string>[];
}) => (
  <>
    {optionsData.map(({ label, value }) => (
      <option key={label} value={value}>
        {label}
      </option>
    ))}
  </>
);

type ComplaintsFormData = z.infer<typeof complaintsFormSchema>;

export const ComplaintsForm = () => {
  const [isCustomer, setIsCustomer] = useState(true);
  const [service, setService] = useState('');
  const [error, setError] = useState(false);
  const errorAlertsRef = useRef<ErrorAlertsRef>(null);
  const router = useRouter();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ComplaintsFormData>({
    resolver: zodResolver(complaintsFormSchema),
    mode: 'onSubmit',
  });

  const onSubmit = async (data: ComplaintsFormData) => {
    try {
      console.log('### data', data);
      if (!isValid) return;
      await sendComplaintsFormRequest({ ...data, service });

      router.push(SUBMISSION_SUCCESS_URL);
    } catch (error) {
      console.error('Submission error:', error);
      setError(true);
    }
  };

  return (
    <Flex direction='column' className={styles['complaints-form']}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {error && (
          <Box margin='lg 0' border={false}>
            <Text size='T300' color='error' data-testid='error-message'>
              There has been an error processing your complaints form. Please
              try again later
            </Text>
          </Box>
        )}
        <Box>
          <Text weight='bold' size='T500' marginBottom>
            Raise a complaint
          </Text>
          <Text size='T300' marginBottom>
            If you’re unhappy with our service, please let us know as soon as
            you can. We’ll do our best to put things right, so you can carry on
            enjoying your services again. We’ll also use your feedback to
            improve our services where we can.
          </Text>

          <Box direction='row' justify='between'>
            <FormSelect
              id='isCustomer'
              description='Are you a customer?'
              {...register('isCustomer')}
              error={errors?.isCustomer}
              aria-placeholder='Select'
              onChange={(e) => {
                setIsCustomer(e.target.value === 'yes');
              }}
              marginBottom
            >
              <Options
                optionsData={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
            </FormSelect>

            {isCustomer && (
              <FormInput
                id='mobileNumber'
                description='Please enter your O2 mobile number'
                {...register('mobileNumber')}
                error={errors?.mobileNumber}
                placeholder='07917112233'
              />
            )}
          </Box>

          <FormRadioGroup
            id='complaintRadio'
            label="Your complaint"
            description="Which of the following services does your complaint relate to?"
            error={errors?.complaintRadio?.message}
          >
            <Box direction='row' justify='center' margin={'0 0 lg 0'}>
              {formOptionInputList.map((item) => (
                <Card
                  bg={isCustomer ? 'primary' : 'secondary'}
                  key={item.name}
                  border
                  padding={'lg'}
                  margin={'sm'}
                  rounded={'sm'}
                  align='center'
                  width={'226px'}
                >
                  <CardHeader align='center'>
                    <Icon icon={item.icon} color='dark' size='lg' />
                  </CardHeader>
                  <CardFooter>
                    <FormRadioInput
                      label={item.label}
                      isFlag={false}
                      isBordered={false}
                      disabled={!isCustomer}
                      value='no'
                      {...register('complaintRadio')}
                      onChange={() => setService(item.name)}
                    />
                  </CardFooter>
                </Card>
              ))}
            </Box>
          </FormRadioGroup>
          <FormSelect
            id='issueDescription'
            label={<Text>What issue best describes your problem?</Text>}
            {...register('issueDescription')}
            error={errors?.issueDescription}
            aria-placeholder='Select'
            marginBottom
          >
            <Options optionsData={complaintsList} />
          </FormSelect>
          <FormSelect
            id='issueType'
            label={<Text>What type of problem do you have?</Text>}
            {...register('issueType')}
            error={errors?.issueType}
            aria-placeholder='Select'
            marginBottom
          >
            <Options
              optionsData={[
                { label: 'Issue type 1', value: 'type1' },
                { label: 'Issue type 2', value: 'type2' },
              ]}
            />
          </FormSelect>
        </Box>
        <Divider color='light' margin={'md 0 lg 0'} />
        <Box margin={'lg 0 lg'}>
          <Text weight='bold' size='T500' marginBottom>
            Your details
          </Text>
          <FormInput
            id='firstName'
            label={<Text>First name</Text>}
            {...register('firstName')}
            error={errors?.firstName}
            marginBottom
          />

          <FormInput
            id='lastName'
            label={<Text>Last name</Text>}
            {...register('lastName')}
            error={errors?.lastName}
            marginBottom
          />

          <Box direction='row' justify='between' margin={'lg 0 lg'}>
            <FormInput
              id='addressLine1'
              label={<Text>Address line 1</Text>}
              {...register('addressLine1')}
              error={errors?.addressLine1}
              width='full-width'
            />
            <FormInput
              id='addressLine2'
              label={<Text>Address line 2</Text>}
              {...register('addressLine2')}
              error={errors?.addressLine2}
              width='full-width'
            />
          </Box>

          <Box direction='row' justify='between' margin={'lg 0 lg'}>
            <FormInput
              id='town'
              label={<Text>Town</Text>}
              {...register('town')}
              error={errors?.town}
            />
            <FormInput
              id='country'
              label={<Text>Country</Text>}
              {...register('country')}
              error={errors?.country}
            />
          </Box>

          <FormInput
            id='postCode'
            label={<Text>Post Code</Text>}
            {...register('postCode')}
            error={errors?.postCode}
          />
        </Box>

        <FormTextarea
          id='issueDetails'
          dataTestId='issueDetails'
          label={<Text>Please provide details of your issue</Text>}
          {...register('issueDetails')}
          placeholder='Type here'
          error={errors?.issueDetails}
          marginBottom
        />

        <FormTextarea
          id='howToFixTheIssue'
          label={<Text>What would you like O2 to do about your issue?</Text>}
          dataTestId='howToFixTheIssue'
          {...register('howToFixTheIssue')}
          placeholder='Type here'
          error={errors?.howToFixTheIssue}
          marginBottom
        />

        <FormInput
          id='contactMobileNumber'
          label={<Text>Contact telephone number</Text>}
          {...register('contactMobileNumber')}
          error={errors?.contactMobileNumber}
          marginBottom
        />
        <Box direction='row' justify='between'>
          <FormInput
            id='email'
            label={<Text>Email address</Text>}
            {...register('email')}
            error={errors?.email}
            marginBottom
          />

          <FormInput
            id='confirmEmail'
            label={<Text>Confirm email address</Text>}
            {...register('confirmEmail')}
            error={errors?.confirmEmail}
            marginBottom
          />
        </Box>

        <FormSelect
          id='preferContact'
          label={<Text>How would you prefer us to contact you?</Text>}
          {...register('preferContact')}
          error={errors?.preferContact}
          aria-placeholder='Yes'
          marginBottom
        >
          <Options
            optionsData={[
              { label: 'Email', value: 'email' },
              { label: 'Call', value: 'call' },
            ]}
          />
        </FormSelect>

        <Button type='submit' width='lg'>
          Submit
        </Button>
      </form>
    </Flex>
  );
};
